const CHANNEL_NAME = "palantir-data-sync";
const STORAGE_KEY = "palantir:data-sync:last-event";
const EVENT_TYPE = "palantir:data-changed";
const FETCH_SYNC_FLAG = "__palantirCrossTabFetchSyncInstalled";
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);
const MAX_SEEN_EVENTS = 80;
const READ_ONLY_POST_PATHS = new Set([
  "/api/purchasing/requisitions/process-preview",
  "/api/reservation-groups/payment-balances/batch",
]);

const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
let sequence = 0;
let broadcastChannel = null;

function canUseBrowserApis() {
  return typeof window !== "undefined";
}

function getBroadcastChannel() {
  if (!canUseBrowserApis() || typeof window.BroadcastChannel !== "function") {
    return null;
  }

  if (!broadcastChannel) {
    try {
      broadcastChannel = new window.BroadcastChannel(CHANNEL_NAME);
    } catch (_error) {
      broadcastChannel = null;
    }
  }

  return broadcastChannel;
}

function createEventDeduper() {
  const seenEventIds = new Set();
  const seenEventQueue = [];

  return (eventId) => {
    if (!eventId || seenEventIds.has(eventId)) return false;

    seenEventIds.add(eventId);
    seenEventQueue.push(eventId);

    while (seenEventQueue.length > MAX_SEEN_EVENTS) {
      const oldEventId = seenEventQueue.shift();
      seenEventIds.delete(oldEventId);
    }

    return true;
  };
}

function normalizeUrl(url) {
  if (!url) return "";

  try {
    const baseUrl = canUseBrowserApis() ? window.location.origin : "http://localhost";
    const parsedUrl = new URL(url, baseUrl);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch (_error) {
    return String(url);
  }
}

function isReadOnlyPostUrl(normalizedUrl) {
  const pathname = String(normalizedUrl || "")
    .split("?")[0]
    .replace(/\/+$/, "")
    .toLowerCase();

  return READ_ONLY_POST_PATHS.has(pathname);
}

export function shouldBroadcastAxiosDataChange(config) {
  return shouldBroadcastDataChange({
    method: config?.method,
    url: config?.url,
    skipCrossTabSync: config?.skipCrossTabSync,
  });
}

function shouldBroadcastDataChange({ method, url, skipCrossTabSync } = {}) {
  const normalizedMethod = String(method || "get").toLowerCase();
  if (!MUTATING_METHODS.has(normalizedMethod)) return false;
  if (skipCrossTabSync) return false;

  const normalizedUrl = normalizeUrl(url);
  if (!normalizedUrl) return false;

  const lowerUrl = normalizedUrl.toLowerCase();
  if (lowerUrl.includes("/api/auth/")) return false;
  if (normalizedMethod === "post" && isReadOnlyPostUrl(lowerUrl)) return false;

  return true;
}

function getFetchRequestMethod(input, init) {
  return init?.method || input?.method || "get";
}

function getFetchRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (input?.url) return input.url;
  return String(input || "");
}

export function installCrossTabFetchBroadcaster() {
  if (!canUseBrowserApis() || typeof window.fetch !== "function") {
    return;
  }

  if (window.fetch[FETCH_SYNC_FLAG]) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  const patchedFetch = async (input, init) => {
    const response = await originalFetch(input, init);
    const method = getFetchRequestMethod(input, init);
    const url = getFetchRequestUrl(input);

    if (
      response?.ok &&
      shouldBroadcastDataChange({
        method,
        url,
        skipCrossTabSync: init?.skipCrossTabSync,
      })
    ) {
      notifyCrossTabDataChanged({
        method,
        url,
        status: response.status,
      });
    }

    return response;
  };

  try {
    Object.defineProperty(patchedFetch, FETCH_SYNC_FLAG, {
      value: true,
      configurable: true,
    });
  } catch (_error) {
    patchedFetch[FETCH_SYNC_FLAG] = true;
  }

  window.fetch = patchedFetch;
}

export function notifyCrossTabDataChanged({ method, url, status } = {}) {
  if (!canUseBrowserApis()) return;

  const event = {
    id: `${tabId}-${Date.now()}-${sequence += 1}`,
    type: EVENT_TYPE,
    sourceTabId: tabId,
    method: String(method || "").toUpperCase(),
    url: normalizeUrl(url),
    status: Number(status || 0),
    ts: Date.now(),
  };

  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage(event);
    } catch (_error) {
      // The localStorage fallback below still covers the tab sync.
    }
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
  } catch (_error) {
    // Some browsers can block storage; BroadcastChannel is enough when present.
  }
}

export function subscribeToCrossTabDataChanges(callback) {
  if (!canUseBrowserApis() || typeof callback !== "function") {
    return () => {};
  }

  const rememberEvent = createEventDeduper();

  const handleEvent = (event) => {
    if (!event || event.type !== EVENT_TYPE) return;
    if (event.sourceTabId === tabId) return;
    if (!rememberEvent(event.id)) return;

    callback(event);
  };

  const channel = getBroadcastChannel();
  const handleChannelMessage = (messageEvent) => {
    handleEvent(messageEvent?.data);
  };

  if (channel) {
    channel.addEventListener("message", handleChannelMessage);
  }

  const handleStorage = (storageEvent) => {
    if (storageEvent.key !== STORAGE_KEY || !storageEvent.newValue) return;

    try {
      handleEvent(JSON.parse(storageEvent.newValue));
    } catch (_error) {
      // Ignore malformed storage events.
    }
  };

  window.addEventListener("storage", handleStorage);

  return () => {
    if (channel) {
      channel.removeEventListener("message", handleChannelMessage);
    }
    window.removeEventListener("storage", handleStorage);
  };
}
